import React, { useCallback, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SearchIcon from '@mui/icons-material/Search'
import ClearIcon from '@mui/icons-material/Clear'
import ImageIcon from '@mui/icons-material/Image'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import TextFieldsIcon from '@mui/icons-material/TextFields'
import ViewCarouselIcon from '@mui/icons-material/ViewCarousel'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import TimerIcon from '@mui/icons-material/Timer'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import { Table, Tag, message } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { useNavigate } from 'react-router-dom'

import { ContentWrapper } from '../../../../components/ContentWrapper'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import useAxios, { axiosInstance } from '../../../../hooks/useAxios'
import PageUrls from '../../../../constants/page-urls'
import DateRangePicker from '../../../../components/DateRangePicker'
import { SalepagePreviewModal } from '../SalepagePreview/SalepagePreviewModal'
import type { IBlock, BlockType } from '../../types'
import { BLOCK_TYPE_META } from '../../types'

export interface ISalepage {
  id: string
  merchant: string
  slug: string
  title: string
  status: 'draft' | 'published'
  blocks?: IBlock[]
  meta?: Record<string, unknown>
  createdAt: string
  updatedAt: string
}

interface SalepageListResponse {
  items: ISalepage[]
  total: number
}

const Menus = [
  {
    breadcrumbName: 'Salepage',
    title: 'รายการ Salepage',
    path: PageUrls.Salepage.Index,
  },
]

const PAGE_SIZE = 15

const BLOCK_ICONS: Record<BlockType, React.ReactNode> = {
  IMAGE: <ImageIcon sx={{ fontSize: 13 }} />,
  VIDEO: <VideoLibraryIcon sx={{ fontSize: 13 }} />,
  CONTENT: <TextFieldsIcon sx={{ fontSize: 13 }} />,
  SLIDER: <ViewCarouselIcon sx={{ fontSize: 13 }} />,
  FORM: <ShoppingCartIcon sx={{ fontSize: 13 }} />,
  COUNTDOWN: <TimerIcon sx={{ fontSize: 13 }} />,
  BANK_ACCOUNTS: <AccountBalanceIcon sx={{ fontSize: 13 }} />,
}

const countBlockTypes = (blocks: IBlock[] = []) => {
  const counts: Partial<Record<BlockType, number>> = {}
  blocks.forEach((b) => {
    counts[b.type] = (counts[b.type] ?? 0) + 1
  })
  return counts
}

const SalepageListPage = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')

  const [previewRecord, setPreviewRecord] = useState<ISalepage | null>(null)

  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    startDate: '',
    endDate: '',
  })

  const params: Record<string, unknown> = {
    skip: (page - 1) * PAGE_SIZE,
    limit: PAGE_SIZE,
  }
  if (appliedFilters.search) params.search = appliedFilters.search
  if (appliedFilters.startDate) params.startDate = appliedFilters.startDate
  if (appliedFilters.endDate) params.endDate = appliedFilters.endDate

  const listRes = useAxios<SalepageListResponse>({
    method: 'GET',
    url: '/salepage',
    params,
  })

  const handleSearch = useCallback(() => {
    setAppliedFilters({
      search: search.trim(),
      startDate,
      endDate,
    })
    setPage(1)
  }, [search, startDate, endDate])

  const handleClearFilters = useCallback(() => {
    setSearch('')
    setStartDate('')
    setEndDate('')
    setAppliedFilters({ search: '', startDate: '', endDate: '' })
    setPage(1)
  }, [])

  const hasFilters =
    !!appliedFilters.search || !!appliedFilters.startDate || !!appliedFilters.endDate

  const handleCreate = useCallback(() => {
    navigate(PageUrls.Salepage.Create)
  }, [navigate])

  const handleDelete = useCallback(
    async (id: string) => {
      if (!window.confirm('ต้องการลบหน้านี้?')) return
      try {
        await axiosInstance.delete(`/salepage/${id}`)
        message.success('ลบสำเร็จ')
        listRes.mutate?.()
      } catch (e: any) {
        message.error(e?.response?.data?.message ?? 'ลบไม่สำเร็จ')
      }
    },
    [listRes],
  )

  const items = listRes.data?.items ?? []
  const total = listRes.data?.total ?? 0

  const columns: ColumnsType<ISalepage> = [
    {
      title: 'ชื่อหน้า',
      dataIndex: 'title',
      key: 'title',
      render: (title: string, record) => (
        <Box>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              cursor: 'pointer',
              '&:hover': { color: 'primary.main' },
            }}
            onClick={() =>
              navigate(`${PageUrls.Salepage.Index}/${record.id}`)
            }
          >
            {title || '—'}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              mt: 0.25,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontFamily: 'monospace' }}
            >
              /s/{record.slug}
            </Typography>
            <Tooltip title="เปิดหน้า Salepage">
              <IconButton
                size="small"
                sx={{ p: 0.25 }}
                onClick={(e) => {
                  e.stopPropagation()
                  window.open(`/s/${record.slug}`, '_blank')
                }}
              >
                <OpenInNewIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ),
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      width: 200,
      align: 'center',
      render: (status: string) => (
        <Tag
          color={status === 'published' ? 'success' : 'default'}
          style={{ margin: 0 }}
        >
          {status === 'published' ? 'Published' : 'Draft'}
        </Tag>
      ),
    },
    {
      title: 'Blocks',
      dataIndex: 'blocks',
      key: 'blocks',
      width: 400,
      render: (_: unknown, record) => {
        const blocks = record.blocks ?? []
        if (blocks.length === 0) {
          return (
            <Typography variant="caption" color="text.disabled">
              ยังไม่มี Block
            </Typography>
          )
        }
        const counts = countBlockTypes(blocks)
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {(Object.keys(counts) as BlockType[]).map((type) => {
              const meta = BLOCK_TYPE_META[type]
              return (
                <Chip
                  key={type}
                  size="small"
                  icon={
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        color: `${meta.color} !important`,
                      }}
                    >
                      {BLOCK_ICONS[type]}
                    </Box>
                  }
                  label={`${meta.label} ${counts[type]}`}
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 600,
                    bgcolor: `${meta.color}18`,
                    color: 'text.secondary',
                    borderColor: `${meta.color}40`,
                    '& .MuiChip-icon': { ml: 0.5 },
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                  variant="outlined"
                />
              )
            })}
          </Box>
        )
      },
    },
    {
      title: 'สร้างเมื่อ',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => (
        <Typography variant="caption" color="text.secondary">
          {v ? new Date(v).toLocaleString('th-TH') : '-'}
        </Typography>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
          <Tooltip title="พรีวิว">
            <IconButton
              size="small"
              onClick={() => setPreviewRecord(record)}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="แก้ไข">
            <IconButton
              size="small"
              color="primary"
              onClick={() =>
                navigate(`${PageUrls.Salepage.Index}/${record.id}`)
              }
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="ลบ">
            <IconButton
              size="small"
              color="error"
              onClick={() => handleDelete(record.id)}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ]

  return (
    <ContentWrapper>
      <Helmet title="รายการ Salepage" />
      <HeaderContent
        title="รายการ Salepage"
        routes={Menus}
        descriptions={`ทั้งหมด ${total} หน้า`}
        childrenRight={
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
          >
            สร้าง Salepage
          </Button>
        }
      />

      <Card
        sx={{
          borderRadius: 2,
          boxShadow: '0px 1px 3px rgba(0,0,0,0.08)',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={1.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                label="ค้นหาชื่อหน้า หรือ slug..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                fullWidth
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18 }} />
                    </InputAdornment>
                  ),
                  ...(search
                    ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearch('')}
                          >
                            <ClearIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }
                    : {}),
                }}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <DateRangePicker
                gap={1}
                startDate={startDate || undefined}
                endDate={endDate || undefined}
                onChange={(s, e) => {
                  setStartDate(s ?? '')
                  setEndDate(e ?? '')
                }}
              />
            </Grid>
            <Grid item xs="auto">
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<SearchIcon />}
                  onClick={handleSearch}
                >
                  ค้นหา
                </Button>
                {hasFilters && (
                  <Button
                    variant="outlined"
                    size="large"
                    startIcon={<ClearIcon />}
                    onClick={handleClearFilters}
                  >
                    ล้างตัวกรอง
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>

        <Table
          rowKey="id"
          loading={listRes.isLoading}
          dataSource={items}
          columns={columns}
          pagination={{
            current: page,
            pageSize: PAGE_SIZE,
            total,
            showSizeChanger: false,
            showTotal: (t) => `ทั้งหมด ${t} รายการ`,
            onChange: (p) => setPage(p),
          }}
          locale={{ emptyText: 'ยังไม่มี Salepage' }}
          size="middle"
        />
      </Card>

      <SalepagePreviewModal
        open={!!previewRecord}
        onClose={() => setPreviewRecord(null)}
        blocks={previewRecord?.blocks ?? []}
        title={previewRecord?.title}
      />
    </ContentWrapper>
  )
}

export default SalepageListPage
